import debounce from 'debounce'
import { useCallback, useRef } from 'react'
import { UseFormReturn, useWatch } from 'react-hook-form'
import useDeepCompareEffect from 'use-deep-compare-effect'

import useComponentWillUnmount from '@/hooks/useComponentWillUnmount'

interface Props {
  form: UseFormReturn<any, any, undefined>
  defaultValues: any
  onSave: any
  interval?: number // milliseconds
  onReset?: () => void
}

const useAutoSave = ({
  form: methods,
  defaultValues,
  onSave,
  interval = 500,
}: Props) => {
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave
  const methodsRef = useRef(methods)
  methodsRef.current = methods

  useComponentWillUnmount(() => {
    if (methodsRef.current.formState.isDirty) {
      methodsRef.current.handleSubmit(onSaveRef.current)()
    }
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce(() => {
      methodsRef.current.handleSubmit(onSaveRef.current)()
    }, interval),
    []
  )

  // Watch all the data, provide with defaultValues from server
  // this way we know if the new data came from server or were actually edited by user
  const watchedData = useWatch({
    control: methods.control,
    defaultValue: defaultValues,
  })

  useDeepCompareEffect(() => {
    if (methodsRef.current.formState.isDirty) {
      debouncedSave()
      debounce(() => {
        methodsRef.current.reset(defaultValues, {
          keepDirtyValues: false,
        })
      }, interval + 20000)
    }
  }, [watchedData])
}

export default useAutoSave
